/**
 * Flat leaf-column spec for the Class Situation CSV export, mirroring the
 * header/body structure rendered by TableSituation
 * (src/pages/assistant/components/TableSituation/TableSituation.js) column
 * for column, group for group, so the exported CSV matches what's on
 * screen. Unlike the on-screen table, every group here is always expanded
 * (Module / Schedule / Class Situation Week / Event) regardless of
 * the collapse state in the UI — only Column Visibility and the active
 * filters affect what gets exported.
 *
 * Keep this file in sync with TableSituation. Any new column, renamed
 * header, or change to the TOTAL/AVERAGE footer must be updated here and
 * in classSituationExport.js in the same change.
 * Gap: the on-screen footer is one row under the matching columns (SUM vs
 * AVG). The CSV still appends that summary as a separate label/value block,
 * not as a last row aligned to these columns.
 */

const showValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : value

const showArray = (value) =>
  Array.isArray(value) && value.length ? value.join(", ") : "-"

const showRecommendToClose = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase()
  if (!normalized) return "-"
  return normalized === "yes" || normalized === "true" ? "Yes" : "No"
}

const showClassStatus = (id) => {
  if (id === null || id === undefined || id === "") return "-"
  if (id === 1 || String(id).trim().toLowerCase() === "planning") return "Planning"
  if (id === 2) return "In progress"
  if (id === "Full" || String(id).trim().toLowerCase() === "full") return "Full"
  if (typeof id === "string" && id.trim()) return id.trim()
  return "Possible to fill"
}

// Text of the row's note(s). At the page level `row.notes` normally holds
// the raw value from the API (an array of {text} entries, or null); the
// canonical single note string, when there's no history array, lives at
// row.nextMonth.notes (see classSituationMappers.js + TableSituation's
// normalizeNotes).
const showNotes = (row) => {
  if (Array.isArray(row?.notes) && row.notes.length > 0) {
    return row.notes.map((n) => (typeof n === "string" ? n : n?.text)).filter(Boolean).join(" | ")
  }
  const text = row?.nextMonth?.notes
  return text === null || text === undefined || text === "" ? "" : String(text)
}

const scheduleLeafColumns = [
  {
    key: "schedule",
    label: "Schedule",
    value: (row) => showValue(row.schedule),
  },
  { key: "status", label: "Class status", value: (row) => showClassStatus(row.status) },
  {
    key: "estimateAcsThisMonth",
    label: "Estimated ACS This Month",
    value: (row) => showValue(row.estimateAcsThisMonth),
  },
  {
    key: "estimateAcsNextMonth",
    label: "Estimated ACS Next Month",
    value: (row) => showValue(row.estimateAcsNextMonth),
  },
  {
    key: "estimateAcsNext2Month",
    label: "Estimated ACS Next 2 Month",
    value: (row) => showValue(row.estimateAcsNext2Month),
  },
  { key: "lessonStudied", label: "Lesson Studied", value: (row) => showValue(row.lessonStudied) },
  {
    key: "finalFillDate",
    label: "Final date to fill student of current Module",
    value: (row) => showValue(row.finalFillDate),
  },
  { key: "csoInCharge", label: "EC in charge", value: (row) => showValue(row.csoInCharge) },
  { key: "teacher", label: "Teacher(s)", value: (row) => showArray(row.teacher) },
  { key: "eppThisMonth", label: "This Month EPP", value: (row) => showValue(row.eppThisMonth) },
  { key: "eppNextMonth", label: "Next Month EPP", value: (row) => showValue(row.eppNextMonth) },
  { key: "eppT8", label: "Next 2 Month EPP", value: (row) => showValue(row.eppT8) },
  {
    key: "recommendToClose",
    label: "Recommend to close",
    value: (row) => showRecommendToClose(row.recommendToClose),
  },
  { key: "hoursPerPeriod", label: "Hours / Lesson", value: (row) => showValue(row.hoursPerPeriod) },
  { key: "hourWeekName", label: "Hour/week name", value: (row) => showValue(row.hourWeekName) },
]

const moduleLeafColumns = ["a", "b"].flatMap((m) => [
  {
    visibilityKey: `module.${m}`,
    group: "Module",
    sub: m.toUpperCase(),
    label: "Start",
    value: (row) => showValue(row.modules?.[m]?.start),
  },
  {
    visibilityKey: `module.${m}`,
    group: "Module",
    sub: m.toUpperCase(),
    label: "Finish",
    value: (row) => showValue(row.modules?.[m]?.finish),
  },
])

const eventLeafColumns = [
  { visibilityKey: "eoccDate", group: "Class situation event", label: "EOCC date", value: (row) => showValue(row.tests?.eoccDate) },
  {
    visibilityKey: "moduleTestDate",
    group: "Class situation event",
    label: "Module test date",
    value: (row) => showValue(row.tests?.moduleTestDate),
  },
  {
    visibilityKey: "unitTestDate",
    group: "Class situation event",
    label: "Unit test date",
    value: (row) => showValue(row.tests?.unitTestDate),
  },
]

// Shared with TableSituation (header, body and TOTAL/AVERAGE footer).
export const CLASS_SITUATION_WEEKS = [
  {
    key: "lastWeek",
    label: "Last week",
    leaves: [
      { label: "ACS", value: (d) => showValue(d?.acs), summary: { key: "avgAcsLastWeek", kind: "avg" } },
      { label: "Attendance", value: (d) => showValue(d?.attendance), summary: { key: "avgLwAttendance", kind: "avg" } },
      {
        label: "Home Learning score",
        value: (d) => showValue(d?.homeLearning?.score),
        summary: { key: "avgLwHomeLearningScore", kind: "avg" },
      },
      {
        label: "Home Learning completion",
        value: (d) => showValue(d?.homeLearning?.completion),
        summary: { key: "avgLwHomeLearningCompletion", kind: "avg" },
      },
      { label: "Offtrack student", value: (d) => showValue(d?.offtrackCount), summary: { key: "totalLwOfftrack", kind: "total" } },
    ],
  },
  {
    key: "thisWeek",
    label: "This week",
    leaves: [
      { label: "ACS", value: (d) => showValue(d?.acs), summary: { key: "avgAcsThisWeek", kind: "avg" } },
      { label: "New student this week", value: (d) => showValue(d?.newStudents), summary: { key: "totalTwNewStudent", kind: "total" } },
      { label: "Outstanding student", value: (d) => showValue(d?.outstandingFeedback), summary: { key: "totalTwFeedback", kind: "total" } },
      { label: "Offtrack student", value: (d) => showValue(d?.offtrackCount), summary: { key: "totalTwOfftrack", kind: "total" } },
      { label: "Retake/Sponsor", value: (d) => showValue(d?.financial?.retakeSponsor), width: 120 },
    ],
  },
  {
    key: "nextWeek",
    label: "Next week",
    leaves: [
      { label: "ACS", value: (d) => showValue(d?.acs), summary: { key: "avgAcsNextWeek", kind: "avg" } },
      { label: "New student this week", value: (d) => showValue(d?.newStudents), summary: { key: "totalNwNewStudent", kind: "total" } },
    ],
  },
]

const weekLeafColumns = CLASS_SITUATION_WEEKS.flatMap((w) =>
  w.leaves.map((leaf) => ({
    visibilityKey: `week.${w.key}.${leaf.label}`,
    group: "Class Situation (Week)",
    sub: w.label,
    label: leaf.label,
    value: (row) => leaf.value(row?.[w.key]),
  }))
)

// Same leaf visibility keys as the column tree (classSituationColumnTree.js).
export function buildClassSituationExportColumns(visibleColumns = []) {
  const isVisible = (accessor) => visibleColumns.includes(accessor)
  const columns = []

  if (isVisible("className"))
    columns.push({ label: "Class name", value: (row) => showValue(row.className) })

  if (isVisible("center")) columns.push({ label: "Center", value: (row) => showValue(row.center) })
  // Hub link — always right after the identity columns.
  columns.push({
    label: "Hub link",
    value: (row) =>
      row.classId === null || row.classId === undefined || row.classId === ""
        ? "-"
        : `https://hub24.apollo.vn:8888/class/detail/${row.classId}`,
  })

  if (isVisible("classLevel"))
    columns.push({ label: "Class level", value: (row) => showValue(row.classLevel) })
  columns.push(...moduleLeafColumns.filter((col) => isVisible(col.visibilityKey)))

  scheduleLeafColumns.forEach((leaf) => {
    if (isVisible(leaf.key)) {
      columns.push({ group: "Schedule", label: leaf.label, value: leaf.value })
    }
  })

  if (isVisible("inProgressClassPerCenter"))
    columns.push({
      label: "Total Class",
      value: (row) => showValue(row.inProgressClassPerCenter),
    })

  columns.push(...eventLeafColumns.filter((col) => isVisible(col.visibilityKey)))

  columns.push(...weekLeafColumns.filter((col) => isVisible(col.visibilityKey)))

  if (isVisible("bookNeeded"))
    columns.push({ label: "Book Needed", value: (row) => showValue(row.bookNeeded) })
  if (isVisible("koc")) columns.push({ label: "KOC", value: (row) => showValue(row.koc) })
  if (isVisible("notes")) columns.push({ label: "Notes", value: (row) => showNotes(row) })

  return columns.map((col) => ({
    ...col,
    path: col.group ? [col.group, ...(col.sub ? [col.sub] : [])] : undefined,
  }))
}
